import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { Stack, Typography, Box, ClickAwayListener, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import { inject, observer } from "mobx-react";
import { c } from "@/theme/colorUtils";
import { useToast } from "@/components/useToast";
import TitleBox from '../CommonComponent/TitleBox';
import CommonSwitch from "@/views/CommonComponent/Styled/CommonSwitch";
import CommonDialog from "@/views/CommonComponent/CommonDialog";
import BaseDialog from "@/views/CommonComponent/BaseDialog";
import CommonLoading from "@/views/CommonComponent/CommonLoading";
import DeviceImage from "@/common/Images/PokoroPen.png";
import {
  DeviceImageWrap,
  SectionTitle,
  InfoRow,
  DeviceInfoLabel,
  ProfileCard,
  MemorySection,
  MemoryRow,
  DisconnectButton,
  DeviceDetailBody,
  DeviceDetailScrollArea,
  DeviceDetailFooter,
  PenNameEditTrigger,
  ProfileSection,
  DeviceInfoSection,
  EmptyProfileCard,
  EmptyProfileText,
  ProfileSetupButton,
  SettingsRestartNote,
} from "./Styled/DeviceDetailStyled";

function useCoarsePointerNoHover() {
  const [coarse, setCoarse] = useState(() => {
    if (typeof window === "undefined") return false;
    return !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCoarse(!mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return coarse;
}

const deviceDetailInfoTooltipTriggerSx = {
  display: "inline-flex",
  alignItems: "center",
  color: c.text02,
  cursor: "help",
  flexShrink: 0,
};

/** 데스크톱은 호버, 터치 기기는 탭으로 툴팁 표시 */
const DeviceDetailInfoTooltip = memo(function DeviceDetailInfoTooltip({ title, ariaLabel, children, sx }) {
  const coarsePointer = useCoarsePointerNoHover();
  const [open, setOpen] = useState(false);

  const triggerSx = useMemo(
    () => (sx ? { ...deviceDetailInfoTooltipTriggerSx, ...sx } : deviceDetailInfoTooltipTriggerSx),
    [sx],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggle = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  if (!coarsePointer) {
    return (
      <Tooltip title={title} arrow enterTouchDelay={0}>
        <Box component="span" sx={triggerSx} tabIndex={0} aria-label={ariaLabel}>
          {children}
        </Box>
      </Tooltip>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Tooltip
          title={title}
          arrow
          open={open}
          onClose={handleClose}
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <Box
            component="span"
            sx={triggerSx}
            tabIndex={0}
            role="button"
            aria-label={ariaLabel}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
          >
            {children}
          </Box>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
});

DeviceDetailInfoTooltip.propTypes = {
  title: PropTypes.node.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

const PendingDeviceDisconnectToastKey = "_PENDING_DEVICE_DISCONNECT_TOAST_";

function DeviceDetail(props) {
  const { intl, authStore, userStore } = props;
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const deviceIdFromState = location.state?.deviceId ?? null;
  const deviceIdFromQuery = searchParams.get("deviceId");
  const resolvedQueryDeviceId =
    deviceIdFromQuery && String(deviceIdFromQuery).trim() !== ""
      ? String(deviceIdFromQuery).trim()
      : null;
  const deviceId = deviceIdFromState ?? resolvedQueryDeviceId;
  const device = userStore.userDeviceDetail;
  const { showErrorToast } = useToast();

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnectGuideModalOpen, setIsDisconnectGuideModalOpen] = useState(false);
  const [isMemoryUpdating, setIsMemoryUpdating] = useState(false);
  const [isWebSearchUpdating, setIsWebSearchUpdating] = useState(false);
  const defaultPenName = intl.formatMessage({ id: "device_detail_default_pen_name" });
  const penName = device?.deviceName === device?.deviceId ? '' : device?.deviceName;
  const serialNumber = device?.deviceId;
  const profileName = device?.profile?.name;
  const profileYear = device?.profile?.birthDate?.toString().slice(0, 4);
  const profileGender =
    device && device.profile
      ? device.profile.gender === "M"
        ? intl.formatMessage({ id: "device_detail_gender_boy" })
        : device.profile.gender === "F"
          ? intl.formatMessage({ id: "device_detail_gender_girl" })
          : device.profile.gender === "U"
            ? intl.formatMessage({ id: "device_detail_gender_unknown" })
            : ""
      : "";
  const profileDetail = intl.formatMessage({ id: "device_detail_birth_year"}, { year: profileYear } ) + " · " + profileGender;

  useEffect(() => {
    if (!deviceId) {
      history.replace("/deviceList");
      return;
    }

    if (authStore.loginUser?.id) {
      userStore.getUserDevice(authStore.loginUser.id, deviceId);
    }
  }, [authStore.loginUser?.id, deviceId, history, userStore]);

  useEffect(() => {
    if (!deviceId || userStore.isUserDeviceLoading) {
      return;
    }

    if (!device) {
      showErrorToast(intl.formatMessage({ id: "device_disconnect_fail_toast" }));
      history.replace("/deviceList");
    }
  }, [deviceId, device, history, intl, showErrorToast, userStore.isUserDeviceLoading]);

  const handleBack = () => {
    const previousPath = location.state?.from;
    history.push(previousPath === "/home" ? "/home" : "/deviceList");
  };

  const handleEditPenName = () => {
    history.push("/penNameEdit", {
      deviceId,
      penName,
      from: location.state?.from,
    });
  };

  const handlePenNameKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleEditPenName();
    }
  };

  const handleProfileClick = () => {
    history.push("/userInfoSetting", {
      from: location.state?.from,
    });
  };

  const handleProfileSetupKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleProfileClick();
    }
  };

  const handleLatestSearchToggle  = async (event) => {
    if (!authStore.loginUser?.id || !device?.profile || isWebSearchUpdating) {
      return;
    }

    setIsWebSearchUpdating(true);
    try {
      await authStore.authRepository.updateUserDeviceProfile(authStore.loginUser.id, {
        deviceId: device.deviceId,
        name: device.profile.name,
        birthDate: device.profile.birthDate,
        gender: device.profile.gender,
        isAgreedToUseUnder14: device.profile.isAgreedToUseUnder14 === true,
        isMemoryDisabled: device.profile.isMemoryDisabled,
        isWebSearchDisabled: !event.target.checked,
      });

      userStore.getUserDevice(authStore.loginUser.id, device.deviceId);
    } catch {} finally {
      setIsWebSearchUpdating(false);
    }
  };

  const handleMemoryToggle = async (event) => {
    if (!authStore.loginUser?.id || !device?.profile || isMemoryUpdating) {
      return;
    }

    setIsMemoryUpdating(true);
    try {
      await authStore.authRepository.updateUserDeviceProfile(authStore.loginUser.id, {
        deviceId: device.deviceId,
        name: device.profile.name,
        birthDate: device.profile.birthDate,
        gender: device.profile.gender,
        isAgreedToUseUnder14: device.profile.isAgreedToUseUnder14 === true,
        isMemoryDisabled: !event.target.checked,
        isWebSearchDisabled: device.profile.isWebSearchDisabled,
      });

      userStore.getUserDevice(authStore.loginUser.id, device.deviceId);
    } catch {} finally {
      setIsMemoryUpdating(false);
    }
  };

  const handleDisconnectClick = () => {
    setIsDisconnectModalOpen(true);
  };

  const handleDisconnectCancel = () => {
    setIsDisconnectModalOpen(false);
  };

  const handleDisconnectGuideOpen = () => {
    setIsDisconnectModalOpen(false);
    setIsDisconnectGuideModalOpen(true);
  };

  const handleDisconnectConfirm = async () => {
    setIsDisconnectGuideModalOpen(false);
    try {
      await userStore.deleteUserDeviceData(authStore.loginUser.id, device?.deviceId);
      localStorage.setItem(PendingDeviceDisconnectToastKey, "true");

      if (
        String(userStore.selectedDevice?.deviceId ?? "") ===
        String(device?.deviceId ?? "")
      ) {
        userStore.setSelectedDevice("");
      }
      userStore.clearCachedUserDeviceList();
      history.goBack();
    } catch {
      showErrorToast(
        intl.formatMessage({ id: "device_disconnect_fail_toast" })
      );
    }
  };

  if (userStore.isUserDeviceLoading || userStore.isDeleteUserDeviceLoading || !device) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CommonLoading />
      </Box>
    );
  }

  return (
    <>
      <Stack width="100%" height="100vh" sx={{ overflow: "hidden" }}>
        <TitleBox
          title={intl.formatMessage({ id: "device_detail_title" })} /* 포코로 상세 정보 */
          subtitle={intl.formatMessage({ id: "device_detail_subtitle" })} /* 포코로 펜의 상세 정보를 제공합니다. */
          onBack={handleBack}
        />

        <DeviceDetailBody>
          <DeviceDetailScrollArea>
          <DeviceInfoSection>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={2}
              sx={{ width: "100%" }}
            >
              <DeviceImageWrap sx={{ flexShrink: 0 }}>
                <img src={DeviceImage} alt="POKORO" />
              </DeviceImageWrap>
              <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                <SectionTitle>
                  {intl.formatMessage({ id: "device_detail_section_device" })}
                </SectionTitle>
                <InfoRow sx={{ columnGap: 1 }}>
                  <DeviceInfoLabel component="span">
                    {intl.formatMessage({ id: "device_detail_pen_name" })}
                  </DeviceInfoLabel>
                  <PenNameEditTrigger
                    onClick={handleEditPenName}
                    onKeyDown={handlePenNameKeyDown}
                    role="button"
                    tabIndex={0}
                    sx={{ flex: 1, minWidth: 0, justifyContent: "flex-end" }}
                  >
                    <Typography
                      variant="body2"
                      color={!penName ? c.text02 : c.text01}
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {penName ? penName : defaultPenName}
                    </Typography>
                    <Box component="span" sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}>
                      <Icon icon="hugeicons:edit-04" fontSize={16} />
                    </Box>
                  </PenNameEditTrigger>
                </InfoRow>
                <InfoRow sx={{ columnGap: 1 }}>
                  <DeviceInfoLabel component="span">
                    {intl.formatMessage({ id: "device_detail_serial" })}
                  </DeviceInfoLabel>
                  <Typography
                    variant="body2"
                    color={c.text01}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "right",
                    }}
                  >
                    {serialNumber}
                  </Typography>
                </InfoRow>
              </Stack>
            </Stack>

              <ProfileSection>
                <SectionTitle>
                  {intl.formatMessage({ id: "device_detail_section_profile" })} {/* 등록된 프로필 정보 */}
                </SectionTitle>
                {device.profile ? (
                  <MemorySection>
                    <ProfileCard onClick={handleProfileClick}>
                      <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                        <Typography variant="body1" sx={{ fontWeight: 700}}>
                          {profileName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: (c.grayHardest) }}>
                          {profileDetail}
                        </Typography>
                      </Stack>
                      <Icon icon="weui:arrow-outlined" fontSize={20} />
                    </ProfileCard>
                    <Stack gap={1}>
                      <MemoryRow sx={{ alignItems: "stretch" }}>
                        <Stack direction="column" spacing={1} sx={{ width: "100%", flex: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            columnGap={1}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {intl.formatMessage({ id: "device_detail_section_memory" })}
                              </Typography>
                              <DeviceDetailInfoTooltip
                                title={intl.formatMessage({ id: "device_detail_memory_tooltip" })}
                                ariaLabel={intl.formatMessage({ id: "device_detail_memory_tooltip" })}
                              >
                                <Icon icon="material-symbols:info-outline" fontSize={18} aria-hidden />
                              </DeviceDetailInfoTooltip>
                            </Stack>
                            <Box sx={{ flexShrink: 0 }}>
                              <CommonSwitch
                                checked={!device.profile.isMemoryDisabled}
                                onChange={handleMemoryToggle}
                                disabled={isMemoryUpdating}
                              />
                            </Box>
                          </Stack>
                          <Typography variant="caption" color={c.text02}>
                            {intl.formatMessage({ id: "device_detail_memory_desc" })}
                          </Typography>
                        </Stack>
                      </MemoryRow>
                      <MemoryRow sx={{ alignItems: "stretch" }}>
                        <Stack direction="column" spacing={1} sx={{ width: "100%", flex: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            columnGap={1}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {intl.formatMessage({ id: "device_detail_latest_search_title" })}
                              </Typography>
                              <DeviceDetailInfoTooltip
                                title={intl.formatMessage({ id: "device_detail_latest_search_tooltip" })}
                                ariaLabel={intl.formatMessage({ id: "device_detail_latest_search_tooltip" })}
                              >
                                <Icon icon="material-symbols:info-outline" fontSize={18} aria-hidden />
                              </DeviceDetailInfoTooltip>
                            </Stack>
                            <Box sx={{ flexShrink: 0 }}>
                              <CommonSwitch
                                  checked={!device.profile.isWebSearchDisabled}
                                onChange={handleLatestSearchToggle}
                                disabled={isWebSearchUpdating}
                              />
                            </Box>
                          </Stack>
                          <Typography variant="caption" color={c.text02}>
                            {intl.formatMessage({ id: "device_detail_latest_search_desc" })}
                          </Typography>
                        </Stack>
                      </MemoryRow>
                    </Stack>
                    <SettingsRestartNote>
                      {intl.formatMessage({ id: "device_detail_settings_restart_note" })}
                    </SettingsRestartNote>
                  </MemorySection>
                ) : (
                  <EmptyProfileCard>
                    <EmptyProfileText>
                      {intl.formatMessage({ id: "device_detail_no_profile" })}
                    </EmptyProfileText>
                    <ProfileSetupButton
                      onClick={handleProfileClick}
                      onKeyDown={handleProfileSetupKeyDown}
                      role="button"
                      tabIndex={0}
                    >
                      {intl.formatMessage({ id: "profile_setup_button" })}
                    </ProfileSetupButton>
                  </EmptyProfileCard>
                )}
              </ProfileSection>
          </DeviceInfoSection>
          </DeviceDetailScrollArea>
          <DeviceDetailFooter>
            <DisconnectButton onClick={handleDisconnectClick}>
              {intl.formatMessage({ id: "device_detail_delete_and_reset" })}
            </DisconnectButton>
          </DeviceDetailFooter>
        </DeviceDetailBody>
        <CommonDialog
          open={isDisconnectModalOpen}
          title={intl.formatMessage({ id: "device_disconnect_confirm_title" })} /* 포코로를 삭제하고 초기화 하시겠습니까? */
          subText={
            <div>
              <p style={{ color: c.error, fontWeight: 700 }}>{intl.formatMessage({ id: "device_disconnect_confirm_text" })}</p>
              {/* 선택한 포코로를 삭제하고 초기화하면 앱에서 제거되며 대화 및 학습 데이터가 영구 삭제됩니다. 삭제된 데이터는 복구할 수 없습니다. */}
              {intl.formatMessage({ id: "device_disconnect_confirm_text2" })}
              {/* 삭제된 데이터는 복구할 수 없습니다. */}
            </div>
          }
          onClick1={handleDisconnectCancel}
          onClick2={handleDisconnectGuideOpen}
          LeftButtonText={intl.formatMessage({ id: "dialog_cancle" })} /* 취소 */
          RightButtonText={intl.formatMessage({ id: "device_detail_reset" })} /* 연결 해제 */
        />
        <BaseDialog
          open={isDisconnectGuideModalOpen}
          content={
            <div>
              <p style={{ color: c.text01, fontWeight: 700 }}>
                {intl.formatMessage({ id: "device_disconnect_power_guide_text" })}
              </p>
              <p style={{ color: c.text02 }}>
                {intl.formatMessage({ id: "device_disconnect_power_guide_text2" })}
              </p>
            </div>
          }
          actionType="single"
          singleButtonText={intl.formatMessage({ id: "dialog_confirmButton" })}
          onSingleClick={handleDisconnectConfirm}
        />
      </Stack>
    </>
  );
}

export default injectIntl(inject("authStore", "userStore")(observer(DeviceDetail)));
